<template>
    <div class="objectives-listing">
        <ListingWrapper
            :title="title"
            :pagination="pagination"
            @update-pagination="updateObjectivesListing"
        >
            <template #listingActions>
                <CompassTooltip
                    :disabled="!createObjectiveButton.disabled"
                    :content="createObjectiveButton.tooltip"
                >
                    <CompassButton
                        :label="createObjectiveButton.label"
                        :disabled="createObjectiveButton.disabled"
                        :data-bot="createObjectiveButton.dataBot"
                        @click="handleCreateObjective"
                    />
                </CompassTooltip>
            </template>
            <template #settings>
                <ObjectivesListingSettingsHeader
                    :filters="filters"
                    @updateActiveFilters="updateActiveFilters"
                    @updateFilter="updateFilter"
                    @updateColumns="updateColumns"
                    @updatePagination="updatePagination"
                />
            </template>

            <template #table>
                <TableLoading v-if="isLoading" />
                <ObjectivesListingTable
                    v-else
                    :columns="columns"
                    :rows="rows"
                    :loading="isLoading"
                    :hasActiveFilters="hasActiveFilters"
                    :hasExpandableRows="hasExpandableRows"
                    :createObjectiveButton="createObjectiveButton"
                    @resetFilters="resetFilters"
                    @sort="updateObjectivesListing"
                />
            </template>
        </ListingWrapper>

        <CompassModal
            size="small"
            :title="$t.__('objectives.listing.ObjectivesListing.delete_objective')"
            :primaryAction="deleteParams.primaryAction"
            :secondaryActions="deleteParams.secondaryActions"
            :visible="deleteParams.displayConfirmDeleteDialog"
            @closed="resetDeleteParams"
            @update:visible="resetDeleteParams"
        >
            <template #default>
                <span class="typographies-paragraph-medium-regular">
                    {{
                        $t.__(
                            'objectives.listing.ObjectivesListing.confirm_deletion_of_an_objective',
                        )
                    }}
                </span>
            </template>
        </CompassModal>

        <ObjectiveUpdatePanel
            v-if="hasObjectiveToUpdate"
            :objective="objectiveToUpdate"
            @close="objectiveToUpdate = null"
            @objectiveUpdated="updateObjectivesListing(currentQueryParams)"
        />
    </div>
</template>
<script>
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import ListingWrapper from 'vcomponents/common/listing/ListingWrapper.vue';
import ObjectivesListingSettingsHeader from 'vcomponents/objectives/listing/settings/ObjectivesListingSettingsHeader.vue';
import AvatarMixins from 'mixins/AvatarMixins';
import objectiveService from 'api/objectives/objectiveService';
import ObjectivesListingTable from 'vcomponents/objectives/listing/ObjectivesListingTable.vue';
import ObjectiveUpdatePanel from 'vcomponents/objectives/update/ObjectiveUpdatePanel.vue';
import { createNamespacedHelpers } from 'vuex';
import { mapWritableState } from 'pinia';
import ListingSettingsStore from 'stores/listingSettingsStore.js';
import { useObjectivesListingStore } from 'piniaStores/objectives/objectivesListingStore';

const { mapState: listingSettingsMapState, mapActions: listingSettingsMapActions } =
    createNamespacedHelpers('listingSettingsStores');

export default {
    components: {
        ListingWrapper,
        ObjectivesListingSettingsHeader,
        ObjectiveUpdatePanel,
        ObjectivesListingTable,
        TableLoading,
    },
    mixins: [AvatarMixins],
    data() {
        const createObjectiveButton = {
            label: this.$t.__(
                'objectives.listing.ObjectivesListing.actions.labels.create_an_objective',
            ),
            callback: '/objectives/create',
            tooltip: this.$t.__(
                'objectives.listing.ObjectivesListing.actions.tooltip.create_an_objective',
            ),
            disabled: true,
            dataBot: 'objectives-listing-create-objective',
        };

        return {
            loading: [],
            createObjectiveButton: createObjectiveButton,
            objectives: [],
            objectiveToUpdate: null,
            title: this.$t.__('objectives.listing.ObjectivesListing.title'),
            header: {
                button: createObjectiveButton,
            },
            filters: [],
            rows: [],
            currentQueryParams: {},
            deleteParams: {
                objectiveToDeleteId: null,
                displayConfirmDeleteDialog: false,
                primaryAction: {
                    label: this.$t.__(
                        'objectives.listing.ObjectivesListing.actions.labels.delete_objective',
                    ),
                    action: () => {
                        this.confirmDeleteObjective();
                    },
                },
                secondaryActions: [
                    {
                        label: this.$t.__('common.actions.cancel'),
                        action: () => {
                            this.resetDeleteParams();
                        },
                    },
                ],
            },
            hasExpandableRows: false,
        };
    },
    computed: {
        ...listingSettingsMapState(['columns', 'pagination', 'sorting']),
        ...mapWritableState(useObjectivesListingStore, ['objectiveToDuplicate']),
        isLoading() {
            return this.loading.length > 0;
        },
        hasActiveFilters() {
            return this.filters.some((filter) => filter.isActive);
        },
        hasObjectiveToUpdate() {
            return this.objectiveToUpdate !== null;
        },
    },
    beforeCreate() {
        if (!this.$store.state.listingSettingsStores) {
            this.$store.registerModule('listingSettingsStores', ListingSettingsStore);
        }

        this.$store.dispatch('listingSettingsStores/initListingSettingsStore', {
            listingName: 'objectives',
            defaultColumns: [
                {
                    code: 'progress',
                    label: this.$t.__(
                        'objectives.listing.ObjectivesListing.columns.labels.progress',
                    ),
                    fixed: 'left',
                    sortable: { orderBy: 'progress', direction: '' },
                    isVisible: true,
                    isDefault: true,
                    isMovable: false,
                    width: '215',
                    order: 0,
                },
                {
                    code: 'expected_amount',
                    label: this.$t.__(
                        'objectives.listing.ObjectivesListing.columns.labels.expected_result',
                    ),
                    alignContent: 'right',
                    sortable: { orderBy: 'expected_amount', direction: '' },
                    isVisible: true,
                    isDefault: true,
                    isMovable: false,
                    width: '175',
                    order: 1,
                },
                {
                    code: 'period',
                    label: this.$t.__('objectives.listing.ObjectivesListing.columns.labels.period'),
                    sortable: { orderBy: 'period', direction: '' },
                    isVisible: true,
                    isDefault: true,
                    isMovable: false,
                    order: 2,
                },
                {
                    code: 'assignee',
                    label: this.$t.__(
                        'objectives.listing.ObjectivesListing.columns.labels.assignee',
                    ),
                    sortable: { orderBy: 'assignee', direction: '' },
                    isVisible: true,
                    isDefault: false,
                    isMovable: true,
                    order: 3,
                },
                {
                    code: 'category',
                    label: this.$t.__(
                        'objectives.listing.ObjectivesListing.columns.labels.category',
                    ),
                    sortable: { orderBy: 'category', direction: '' },
                    isVisible: true,
                    isDefault: false,
                    isMovable: true,
                    order: 4,
                },
                {
                    code: 'name',
                    label: this.$t.__('objectives.listing.ObjectivesListing.columns.labels.name'),
                    sortable: { orderBy: 'name', direction: '' },
                    isVisible: true,
                    isDefault: false,
                    isMovable: true,
                    order: 5,
                },
                {
                    code: 'duration',
                    label: this.$t.__(
                        'objectives.listing.ObjectivesListing.columns.labels.duration',
                    ),
                    sortable: { orderBy: 'duration', direction: '' },
                    isVisible: true,
                    isDefault: false,
                    isMovable: true,
                    order: 6,
                },
                {
                    code: 'item',
                    label: this.$t.__('objectives.listing.ObjectivesListing.columns.labels.item'),
                    sortable: { orderBy: 'item', direction: '' },
                    isVisible: true,
                    isDefault: false,
                    isMovable: true,
                    order: 7,
                },
            ],
            defaultPagination: {
                limit: 25,
                offset: 0,
                total: 0,
                currentPage: 1,
            },
        });
    },
    created() {
        this.currentQueryParams = {
            limit: this.pagination.limit,
            offset: 0,
        };

        if (this.sorting && !!this.sorting.order) {
            this.currentQueryParams = {
                ...this.currentQueryParams,
                order: this.sorting.order,
                direction: this.sorting.direction,
            };
        }

        this.getMetaDatas();
        this.fetch(this.currentQueryParams);
    },
    methods: {
        ...listingSettingsMapActions({
            updateStoreColumns: 'updateColumns',
            updateStorePagination: 'updatePagination',
            resetPagination: 'resetPagination',
        }),
        addLoading(loadingCode) {
            this.loading.push(loadingCode);
        },
        removeLoading(loadingCode) {
            this.loading = this.loading.filter((loading) => loading !== loadingCode);
        },
        getMetaDatas() {
            this.addLoading('ObjectivesListing:getMetaDatas');

            objectiveService
                .getMetaDatas()
                .then((response) => {
                    const datas = Object.values(response.data);

                    if (
                        datas.some((data) =>
                            data._links.some((link) => link.rel === 'objective/create'),
                        )
                    ) {
                        this.createObjectiveButton.disabled = false;
                        this.header.button.disabled = false;
                    }

                    this.setFiltersData(datas);
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('common.labels.error_retry'),
                    });
                })
                .finally(() => {
                    this.removeLoading('ObjectivesListing:getMetaDatas');
                });
        },
        fetch(queryParams) {
            this.addLoading('ObjectivesListing:fetch');

            if (queryParams) {
                this.updateQueryParams(queryParams);
            }

            objectiveService
                .getObjectives(this.currentQueryParams)
                .then((response) => {
                    const responseDatas = response.data;
                    this.objectives = responseDatas.data;
                    this.setPaginationDatas(responseDatas.pagination);
                    this.setContentDatas();
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('common.labels.error_retry'),
                    });
                })
                .finally(() => {
                    this.removeLoading('ObjectivesListing:fetch');
                });
        },
        filterObjectives(queryParams) {
            this.addLoading('ObjectivesListing:filter');

            if (queryParams) {
                this.updateQueryParams(queryParams);
            }

            objectiveService
                .searchObjectives(this.currentQueryParams, this.prepareDataParamsForSearch())
                .then((response) => {
                    this.objectives = response.data.data;
                    this.setPaginationDatas(response.data.pagination);
                    this.setContentDatas();
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('common.labels.error_retry'),
                    });
                })
                .finally(() => {
                    this.removeLoading('ObjectivesListing:filter');
                });
        },
        setPaginationDatas(pagination) {
            this.updateStorePagination({
                limit: pagination.limit,
                offset: pagination.offset + pagination.count,
                total: pagination.total,
                currentPage: Math.floor(pagination.offset / pagination.limit) + 1,
            });
        },
        setContentDatas() {
            const formatedDatas = [];
            for (const objective of this.objectives) {
                if (!objective.turnover) {
                    continue;
                }

                let itemName = '-';
                if (objective.item) {
                    itemName = objective.item.name;
                } else if (objective.item_category) {
                    itemName = objective.item_category.name;
                }

                const isObjectiveExpired = moment().isAfter(moment(objective.period.date_end));

                const formatedData = {
                    id: objective.id,
                    category: this.formatCategory(objective.category.code),
                    progress: this.formatProgress(objective.turnover),
                    expected_amount: this.formatAmount(objective.turnover.expected),
                    period: this.formatPeriod(objective.period),
                    name: objective.name,
                    duration: this.formatDuration(objective.period.duration),
                    assignee: this.formatAssignee(objective.assignee),
                    item: itemName,
                    actions: [
                        {
                            label: this.$t.__('common.actions.edit'),
                            onClick: () => {
                                this.displayUpdateObjectivePanel(objective.id);
                            },
                            tooltip: this.$t.__(
                                isObjectiveExpired
                                    ? 'objectives.listing.ObjectivesListing.actions.tooltip.edit_objective.expired_objective'
                                    : 'objectives.listing.ObjectivesListing.actions.tooltip.edit_objective.only_admin',
                            ),
                            disabled:
                                !objective.links.some((link) => link.rel === 'objectives/update') ||
                                isObjectiveExpired,
                        },
                        {
                            label: this.$t.__('common.actions.duplicate'),
                            onClick: () => {
                                this.objectiveToDuplicate = this.objectives.find(
                                    (currentObjective) => currentObjective.id === objective.id,
                                );
                                this.$router.push({ name: 'objectives.create' });
                            },
                            tooltip: this.$t.__(
                                'objectives.listing.ObjectivesListing.actions.tooltip.no_permission_to_duplicate',
                            ),
                            disabled: !objective.links.some(
                                (link) => link.rel === 'objectives/duplicate',
                            ),
                        },
                        {
                            label: this.$t.__('common.actions.delete'),
                            onClick: () => {
                                this.deleteObjective(objective.id);
                            },
                            tooltip: this.$t.__(
                                'objectives.listing.ObjectivesListing.actions.tooltip.delete_objective',
                            ),
                            disabled: !objective.links.some(
                                (link) => link.rel === 'objectives/deleted',
                            ),
                        },
                    ],
                    subrows: [],
                    subrowColumns: [],
                    subrowsVisible: false,
                };

                if (objective.refinements.length > 0) {
                    this.hasExpandableRows = true;
                    formatedData.subrowColumns = [
                        {
                            code: 'progress',
                            label: this.$t.__(
                                'objectives.listing.ObjectivesListing.columns.labels.progress',
                            ),
                            width: '215',
                            order: 0,
                        },
                        {
                            code: 'expected_amount',
                            label: this.$t.__(
                                'objectives.listing.ObjectivesListing.columns.labels.expected_result',
                            ),
                            alignContent: 'right',
                            width: '175',
                            order: 1,
                        },
                        {
                            code: 'period',
                            label: this.$t.__(
                                'objectives.listing.ObjectivesListing.columns.labels.period',
                            ),
                            order: 2,
                        },
                    ];

                    for (const refinement of objective.refinements) {
                        formatedData.subrows.push({
                            progress: this.formatProgress(refinement.turnover),
                            expected_amount: this.formatAmount(refinement.turnover.expected),
                            period: this.formatPeriod(refinement.period),
                        });
                    }
                }

                formatedDatas.push(formatedData);
            }

            this.rows = formatedDatas;
        },
        setFiltersData(objectiveTypes) {
            const categoryFilter = {
                code: 'category',
                label: this.$t.__('objectives.listing.ObjectivesListing.filters.labels.category'),
                activeLabel: this.$t.__(
                    'objectives.listing.ObjectivesListing.filters.activeLabels.category',
                ),
                isActive: false,
                displayDropdown: true,
                optionsSelected: [],
                value: [],
                component: 'ListingFiltersSelect',
                options: [],
            };
            const nameFilter = {
                code: 'name',
                label: this.$t.__('objectives.listing.ObjectivesListing.filters.labels.name'),
                activeLabel: this.$t.__(
                    'objectives.listing.ObjectivesListing.filters.activeLabels.name',
                ),
                isActive: false,
                displayDropdown: true,
                optionsSelected: [],
                value: [],
                component: 'ListingFiltersSearch',
            };
            const assigneeFilter = {
                code: 'assignee',
                label: this.$t.__('objectives.listing.ObjectivesListing.filters.labels.assignee'),
                activeLabel: this.$t.__(
                    'objectives.listing.ObjectivesListing.filters.activeLabels.assignee',
                ),
                isActive: false,
                displayDropdown: true,
                optionsSelected: [],
                value: [],
                component: 'ListingFiltersSelectUsers',
                options: [],
                filterable: true,
            };

            for (const objectiveType of objectiveTypes) {
                categoryFilter.options.push({
                    ...objectiveType.data.category,
                    label: this.formatCategory(objectiveType.data.category.code),
                    id: objectiveType.data.category.code,
                });

                const objectiveTypeFilters = Object.values(objectiveType.filters);
                if (objectiveTypeFilters && objectiveTypeFilters.length > 0) {
                    for (const filter of objectiveTypeFilters) {
                        switch (filter.code) {
                            case 'name':
                                if (
                                    !this.filters.some(
                                        (globalFilter) => globalFilter.code === 'name',
                                    )
                                ) {
                                    this.filters.push(nameFilter);
                                }
                                break;
                            case 'assignee':
                                assigneeFilter.options = this.mergeAssigneeFilterOptions(
                                    assigneeFilter.options,
                                    this.formatAssigneeFilters(filter.items),
                                );
                                break;
                            default:
                                break;
                        }
                    }
                }
            }

            if (Object.values(assigneeFilter.options).length > 0) {
                this.filters.push(assigneeFilter);
            }

            if (categoryFilter.options.length > 0) {
                this.filters.push(categoryFilter);
            }
        },
        formatProgress(progress) {
            if (!progress) return;

            const daysLeft = progress.days_left.toString();
            const actualAmount = this.formatAmount(progress.actual);
            const expectedAmount = this.formatAmount(progress.expected);
            return {
                progressBarState: progress.progression,
                progressBarCaption: this.$t.n__(
                    'objectives.listing.ObjectivesListing.progress_bar.days_left',
                    null,
                    daysLeft,
                    daysLeft,
                ),
                progressBarType: 'colored',
                tooltipContent: this.$t.__(
                    'objectives.listing.ObjectivesListing.progress_bar.tooltip',
                    {
                        actualAmount: actualAmount,
                        expectedAmount: expectedAmount,
                    },
                ),
                tooltipPlacement: 'bottom-start',
            };
        },
        formatAmount(amountToFormat) {
            return this.$filters.numberFormatCurrencyCustomNumberFormat(amountToFormat.amount, {
                currency: amountToFormat.currency,
                currencysymbol: window.Currency.getCurrencySymbol(amountToFormat.currency),
            });
        },
        formatPeriod(dates) {
            const start = this.$filters.dateFormatter(
                moment(dates.date_start).unix(),
                'SLASHED_DATE',
                false,
            );
            const end = this.$filters.dateFormatter(
                moment(dates.date_end).unix(),
                'SLASHED_DATE',
                false,
            );

            return `${start} - ${end}`;
        },
        formatDuration(duration) {
            let durationLabel = '';
            switch (duration) {
                case 'year':
                    durationLabel = this.$t.__('common.labels.a_year');
                    break;
                case 'quarter':
                    durationLabel = this.$t.__('common.labels.a_quarter');
                    break;
                case 'month':
                    durationLabel = this.$t.__('common.labels.a_month');
                    break;
            }
            return durationLabel;
        },
        formatCategory(categoryCode) {
            let categoryLabel = '';
            switch (categoryCode) {
                case 'turnover':
                    categoryLabel = this.$t.__('common.labels.objective.turnover');
                    break;
                case 'forecast_turnover':
                    categoryLabel = this.$t.__('common.labels.objective.forecast_turnover');
                    break;
            }
            return categoryLabel;
        },
        formatAssignee(assignee) {
            let assigneeData = '-';

            if (assignee?.id && assignee?.name?.length > 0) {
                const name = assignee.name.split(' ');
                assigneeData = {
                    id: assignee.id,
                    type: assignee.type,
                    size: 'extra-small',
                    firstname: name[0],
                    lastname: name.slice(1).join(' '),
                    colorType: this.avatarColor(assignee.name),
                    initials: name[0].slice(0, 1).toUpperCase(),
                };
            } else if (assignee.type === 'corp') {
                assigneeData = {
                    id: -1,
                    type: assignee.type,
                    size: 'extra-small',
                    firstname: this.$t.__('common.labels.corp'),
                    lastname: '',
                    colorType: this.avatarColor(assignee.type),
                    initials: '',
                };
            }

            return assigneeData;
        },
        formatAssigneeFilters(assigneeFilters) {
            const assigneeFiltersFormated = {};
            for (const assigneeFilter of assigneeFilters) {
                if (assigneeFilter.code === 'corp') {
                    assigneeFiltersFormated.corp = assigneeFilter;
                } else if (Array.isArray(assigneeFilter) && assigneeFilter.length > 0) {
                    for (const assigneeFilterItem of assigneeFilter) {
                        if (!assigneeFiltersFormated.hasOwnProperty(assigneeFilterItem.code)) {
                            assigneeFiltersFormated[assigneeFilterItem.code] = [];
                        }

                        switch (assigneeFilterItem.code) {
                            case 'staff':
                                var name = assigneeFilterItem.label.split(' ');
                                assigneeFiltersFormated[assigneeFilterItem.code].push({
                                    ...assigneeFilterItem,
                                    firstname: name[0],
                                    lastname: name.slice(1).join(' '),
                                    colorType: this.avatarColor(assigneeFilterItem.label),
                                    initials: name[0].slice(0, 1).toUpperCase(),
                                    avatar: assigneeFilterItem.avatar,
                                });
                                break;
                            case 'group':
                                assigneeFiltersFormated[assigneeFilterItem.code].push(
                                    assigneeFilterItem,
                                );
                                break;
                            default:
                                break;
                        }
                    }
                }
            }

            return assigneeFiltersFormated;
        },
        mergeAssigneeFilterOptions(currentAssigneeFilters, assigneeFiltersToMerge) {
            const assigneeFilters = { ...currentAssigneeFilters };

            for (const assigneeFilterToMerge of Object.values(assigneeFiltersToMerge)) {
                if (assigneeFilterToMerge.code === 'corp' && !assigneeFilters.corp) {
                    assigneeFilters.corp = assigneeFilterToMerge;
                } else if (
                    Array.isArray(assigneeFilterToMerge) &&
                    assigneeFilterToMerge.length > 0
                ) {
                    for (const assigneeFilterItem of assigneeFilterToMerge) {
                        if (!assigneeFilters.hasOwnProperty(assigneeFilterItem.code)) {
                            assigneeFilters[assigneeFilterItem.code] = [];
                        }

                        if (
                            !assigneeFilters[assigneeFilterItem.code].some(
                                (item) => item.value === assigneeFilterItem.value,
                            )
                        ) {
                            assigneeFilters[assigneeFilterItem.code].push(assigneeFilterItem);
                        }
                    }
                }
            }

            return assigneeFilters;
        },
        deleteObjective(objectiveId) {
            this.deleteParams.objectiveToDeleteId = objectiveId;
            this.deleteParams.displayConfirmDeleteDialog = true;
        },
        resetDeleteParams() {
            this.deleteParams.objectiveToDeleteId = null;
            this.deleteParams.displayConfirmDeleteDialog = false;
        },
        confirmDeleteObjective() {
            this.addLoading('ObjectivesListing:delete');
            objectiveService
                .deleteObjective(this.deleteParams.objectiveToDeleteId)
                .then(() => {
                    this.updateObjectivesListing(this.currentQueryParams);
                    this.resetDeleteParams();
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('common.labels.error_retry'),
                    });
                })
                .finally(() => {
                    this.removeLoading('ObjectivesListing:delete');
                });
        },
        handleCreateObjective() {
            this.$router.push({
                path: this.createObjectiveButton.callback,
                params: {},
            });
        },
        updateActiveFilters(filterToUpdate, updateListing = false) {
            for (const filter of this.filters) {
                filter.displayDropdown = false;
                if (filter.code === filterToUpdate.code) {
                    filter.isActive = filterToUpdate.isActive;
                    filter.value = filterToUpdate.value;
                    filter.optionsSelected = filterToUpdate.optionsSelected;
                    filter.displayDropdown = filterToUpdate.displayDropdown;
                }
            }

            if (updateListing) {
                this.updateObjectivesListing(this.currentQueryParams);
            }
        },
        updateFilter({ filterCode, filterValue, filterOptionsSelected }) {
            this.resetPagination();
            for (const filter of this.filters) {
                if (filter.code === filterCode) {
                    filter.value = filterValue;
                    filter.optionsSelected = filterOptionsSelected;
                }
            }

            this.updateObjectivesListing({ ...this.currentQueryParams, offset: 0 });
        },
        resetFilters() {
            this.resetPagination();
            for (const filter of this.filters) {
                filter.isActive = false;
                filter.value = [];
                filter.optionsSelected = [];
            }

            this.updateObjectivesListing({ ...this.currentQueryParams, offset: 0 });
        },
        updateObjectivesListing(queryParams) {
            this.objectiveToUpdate = null;
            if (this.hasActiveFilters) {
                this.filterObjectives(queryParams);
            } else {
                this.fetch(queryParams);
            }
        },
        updateQueryParams(newQueryParams) {
            if (newQueryParams.order) {
                const newColumns = this.columns;
                for (const column of newColumns) {
                    column.sortable.direction = '';
                    if (column.sortable && column.sortable.orderBy === newQueryParams.order) {
                        column.sortable.direction = newQueryParams.direction;
                    }
                }

                this.updateStoreColumns(newColumns);
            }

            this.currentQueryParams = Object.assign(this.currentQueryParams, newQueryParams);
        },
        prepareDataParamsForSearch() {
            const dataParamsForSearch = {
                filters: {},
            };

            const filterName = this.filters.find((filter) => filter.code === 'name');
            if (filterName && filterName.optionsSelected.length > 0) {
                dataParamsForSearch.filters.name = {
                    mode: 'contain',
                    value: filterName.optionsSelected.join(),
                };
            }

            const filterAssignee = this.filters.find((filter) => filter.code === 'assignee');
            if (filterAssignee && filterAssignee.optionsSelected.length > 0) {
                dataParamsForSearch.filters.assignee = filterAssignee.optionsSelected.map(
                    (optionSelected) => {
                        const optionFormated = {
                            type: optionSelected.code,
                        };

                        if (optionSelected.code !== 'corp') {
                            optionFormated.id = parseInt(optionSelected.value);
                        }

                        return optionFormated;
                    },
                );
            }

            const filterCategory = this.filters.find((filter) => filter.code === 'category');
            if (filterCategory && filterCategory.optionsSelected.length > 0) {
                dataParamsForSearch.filters.category = filterCategory.optionsSelected.map(
                    (option) => ({
                        code: option,
                    }),
                );
            }

            return dataParamsForSearch;
        },
        displayUpdateObjectivePanel(objectiveId) {
            this.objectiveToUpdate = this.objectives.find(
                (objective) => objective.id === objectiveId,
            );
        },
        updateColumns(newColumns) {
            this.updateStoreColumns(newColumns);
        },
        updatePagination(newPagination) {
            const newPaginationLimit = parseInt(newPagination.limit);
            if (this.pagination.limit !== newPaginationLimit) {
                this.currentQueryParams = {
                    ...this.currentQueryParams,
                    limit: newPagination.limit,
                    offset: 0,
                };
                this.updateObjectivesListing(this.currentQueryParams);

                this.updateStorePagination({
                    ...this.pagination,
                    limit: newPaginationLimit,
                });
            }
        },
    },
};
</script>

<style>
html > body div.global:has(.objectives-listing) {
    background-color: var(--colors-common-background-app) !important;
}
</style>
